import { CommonRequest, CommonResponse, Method, ServiceName } from 'models/common/RestApi';
import { callApi } from 'utils/ApiUtil';
import {
  PrjSchedule,
  PrjScheduleBox,
  PrjScheduleCondition,
  PrjScheduleEqp,
} from 'models/setup/PrjSchedule';
import { ProjectExcp } from 'models/admin/ExcpPartner';

/**
 * @desc 셋업프로젝트 > 일정관리 API
 * @author cnsglee
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.08.03   cnsglee        신규 생성 (BE 엔드포인트 스펙은 README_적용가이드.md 참조)
 *
 * ※ 프로젝트 콤보는 기존 apis/setup/SetupProject.findDisplayProject,
 *   협력업체 콤보는 기존 apis/setup/SetupSchedule.findExcpByElmPrjId 를 재사용한다.
 */

/**
 * 프로젝트 List 협력사 정보 조회
 * @param elmPrjIds
 */
export const findExcpByElmPrjList = async (elmPrjIds: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/prj-schedule/excp`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ elmPrjIds }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as ProjectExcp[];
};

/**
 * 일정 목록 조회
 * BE: TB_EELMB_PRJ_SCHD_M + (+) 외부조인(PRJ_M/ELM_EXCP_M/PRJ_TGT_EQP_M 명칭 병합)
 */
export const findPrjScheduleList = async (condition?: PrjScheduleCondition) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/prj-schedule`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...(condition || {}) }),
  };

  const response: CommonResponse<PrjSchedule[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as PrjSchedule[];
};

/**
 * 일정 상세 조회
 */
export const findPrjSchedule = async (schdId: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/prj-schedule/${schdId}`,
    serviceName: ServiceName.ELM_BE,
  };

  const response: CommonResponse<PrjSchedule> = await callApi(request);
  return response;
};

/**
 * 일정 저장 (등록/수정 — crudCode로 구분)
 * BE: INSERT/UPDATE TB_EELMB_PRJ_SCHD_M (SQL은 README_적용가이드.md의 검증본 참조)
 */
export const savePrjSchedule = async (schedule: PrjSchedule | undefined) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/prj-schedule`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: schedule,
  };
  const response: CommonResponse<string> = await callApi(request);
  return response;
};

/**
 * 일정 복사 (미완료된 대상설비들만 가져와서 일정 복사)
 */
export const copyPrjSchedule = async (schedule: PrjSchedule | undefined) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/prj-schedule/copy`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: schedule,
  };
  const response: CommonResponse<string> = await callApi(request);
  return response;
};

/**
 * 일정 목록 삭제 (등록/수정 — crudCode로 구분)
 * BE: INSERT/UPDATE TB_EELMB_PRJ_SCHD_M (SQL은 README_적용가이드.md의 검증본 참조)
 */
export const deletePrjSchedule = async (schedule: PrjSchedule[] | undefined) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/prj-schedule/delete`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: schedule,
  };
  const response: CommonResponse<string> = await callApi(request);
  return response;
};

/**
 * 일정 완료 처리 (SCHD_STAT_CD → 'CPLT')
 */
export const completePrjSchedule = async (schdId: string) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/prj-schedule/complete`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: { schdId },
  };
  const response: CommonResponse<string> = await callApi(request);
  return response;
};

/**
 * 대상 설비 목록 (선택 프로젝트 기준, EQP_TP_CD='EQP', 협력업체 선택 시 EXCP_ID 필터)
 */
export const findPrjTargetEquipment = async (
  elmPrjId: string,
  excpId?: string,
  wktTpCd?: string
) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/prj-schedule/target-equipment`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      excpId: excpId || '',
      wktTpCd: wktTpCd || '',
    }),
  };

  const response: CommonResponse<PrjScheduleEqp[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as PrjScheduleEqp[];
};

/**
 * 대상 설비 목록 Box
 */
export const findPrjTargetEquipmentBox = async (condition: PrjScheduleBox) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/prj-schedule/target-equipment/box`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...condition }),
  };

  const response: CommonResponse<PrjScheduleBox[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as PrjScheduleBox[];
};

/**
 * 일정 상세 > 대상설비 완료 처리
 */
export const completePrjScheduleTgtEqp = async (list: PrjScheduleEqp[]) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/prj-schedule/target-equipment/complete`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: list,
  };
  const response: CommonResponse<string> = await callApi(request);
  return response;
};
